import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useResendVerification } from '@/hooks/useApi';
import toast from 'react-hot-toast';

export default function ConfirmAccountPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const resend = useResendVerification();
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    try {
      await resend.mutateAsync({ email });
      toast.success(t('auth.verificationResent'));
      setCooldown(60);
    } catch {
      toast.error(t('auth.resendFailed'));
    }
  };

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.checkYourEmail')}</h2>
      <p className="text-sm text-gray-600 mb-2">{t('auth.verificationSent')}</p>
      {email && (
        <p className="text-sm font-medium text-gray-900 mb-4">{email}</p>
      )}
      <p className="text-sm text-gray-500 mb-8">{t('auth.verificationInstructions')}</p>

      <button
        onClick={handleResend}
        disabled={cooldown > 0 || resend.isPending}
        className="btn-primary w-full mb-4"
      >
        {resend.isPending
          ? t('common.loading')
          : cooldown > 0
            ? `${t('auth.resendVerification')} (${cooldown}s)`
            : t('auth.resendVerification')}
      </button>

      <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500 font-medium">
        {t('auth.backToLogin')}
      </Link>
    </div>
  );
}
